import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, DollarSign, Activity, TrendingUp, Zap, BarChart3, Clock, AlertCircle, RefreshCw, Euro, PoundSterling, Banknote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { useAuth } from '@/context/AuthContext';

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€', USD: '$', GBP: '£', AED: 'د.إ', JPY: '¥', CNY: '¥',
  INR: '₹', KRW: '₩', TRY: '₺', BRL: 'R$', CHF: 'CHF', CAD: 'CA$',
  AUD: 'A$', SEK: 'kr', NOK: 'kr', DKK: 'kr', PLN: 'zł', CZK: 'Kč',
};

const getCurrencySymbol = (code: string) => CURRENCY_SYMBOLS[code] || code;

interface EnrichmentLog {
  id: string;
  entity_type: string;
  entity_id: string;
  company_id: string | null;
  operation: string;
  status: string;
  firecrawl_map_credits: number;
  firecrawl_scrape_credits: number;
  openai_tokens_input: number;
  openai_tokens_output: number;
  openai_model: string | null;
  estimated_cost_usd: number;
  duration_ms: number | null;
  pages_discovered: number;
  pages_selected: number;
  pages_scraped: number;
  error_message: string | null;
  created_at: string;
}

const CHART_COLORS = [
  'hsl(226, 71%, 40%)',
  'hsl(199, 89%, 48%)',
  'hsl(262, 83%, 58%)',
  'hsl(142, 71%, 45%)',
  'hsl(38, 92%, 50%)',
  'hsl(0, 84%, 60%)',
];

export function LeadAnalyticsDashboard() {
  const [logs, setLogs] = useState<EnrichmentLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('all');
  const [leadCounts, setLeadCounts] = useState({ total: 0, webSearched: 0, deepResearched: 0, crawled: 0 });
  const [currency, setCurrency] = useState('EUR');
  const { profile } = useAuth();
  const cs = getCurrencySymbol(currency);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Calculate cutoff date for server-side filtering
      let cutoffDate: string | null = null;
      if (timeRange !== 'all') {
        const now = new Date();
        const cutoff = new Date();
        if (timeRange === '7d') cutoff.setDate(now.getDate() - 7);
        else if (timeRange === '30d') cutoff.setDate(now.getDate() - 30);
        else if (timeRange === 'today') cutoff.setHours(0, 0, 0, 0);
        cutoffDate = cutoff.toISOString();
      }

      // Paginated fetch of ALL enrichment logs (bypasses 1000-row limit)
      const PAGE_SIZE = 1000;
      let allLogs: EnrichmentLog[] = [];
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        let query = supabase
          .from('enrichment_logs')
          .select('*')
          .order('created_at', { ascending: false })
          .range(from, from + PAGE_SIZE - 1);

        if (cutoffDate) {
          query = query.gte('created_at', cutoffDate);
        }

        const { data, error } = await query;

        if (error || !data) {
          console.error('Failed to fetch enrichment logs page:', error);
          break;
        }

        allLogs = allLogs.concat(data as unknown as EnrichmentLog[]);
        hasMore = data.length === PAGE_SIZE;
        from += PAGE_SIZE;
      }

      setLogs(allLogs);

      // Fetch lead pipeline counts
      const { count: total } = await supabase.from('leads').select('*', { count: 'exact', head: true });
      const { count: ws } = await supabase.from('leads').select('*', { count: 'exact', head: true }).eq('web_search_status', 'completed');
      const { count: dr } = await supabase.from('leads').select('*', { count: 'exact', head: true }).eq('deep_research_status', 'completed');
      const { count: cr } = await supabase.from('leads').select('*', { count: 'exact', head: true }).eq('crawl_status', 'completed');
      setLeadCounts({ total: total || 0, webSearched: ws || 0, deepResearched: dr || 0, crawled: cr || 0 });

      // Fetch company currency
      if (profile?.company_id) {
        const { data: companyData } = await supabase
          .from('companies')
          .select('currency')
          .eq('id', profile.company_id)
          .single();
        if (companyData?.currency) setCurrency(companyData.currency);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [timeRange]);

  // Logs are already filtered server-side by timeRange, use directly
  const filteredLogs = logs;

  const stats = useMemo(() => {
    const completed = filteredLogs.filter(l => l.status === 'completed');
    const failed = filteredLogs.filter(l => l.status === 'failed');
    const totalCost = filteredLogs.reduce((sum, l) => sum + Number(l.estimated_cost_usd || 0), 0);
    const uniqueEntities = new Set(completed.map(l => l.entity_id)).size;
    const avgCostPerEntity = uniqueEntities > 0 ? totalCost / uniqueEntities : 0;
    const avgDuration = completed.length > 0
      ? completed.reduce((sum, l) => sum + (l.duration_ms || 0), 0) / completed.length / 1000
      : 0;
    const successRate = filteredLogs.length > 0 ? (completed.length / filteredLogs.length) * 100 : 0;
    const totalScrapeCredits = filteredLogs.reduce((sum, l) => sum + (l.firecrawl_scrape_credits || 0), 0);
    const totalMapCredits = filteredLogs.reduce((sum, l) => sum + (l.firecrawl_map_credits || 0), 0);
    const totalTokensIn = filteredLogs.reduce((sum, l) => sum + (l.openai_tokens_input || 0), 0);
    const totalTokensOut = filteredLogs.reduce((sum, l) => sum + (l.openai_tokens_output || 0), 0);
    const totalPagesDiscovered = filteredLogs.reduce((sum, l) => sum + (l.pages_discovered || 0), 0);
    const totalPagesScraped = filteredLogs.reduce((sum, l) => sum + (l.pages_scraped || 0), 0);

    return {
      totalCost, avgCostPerEntity, avgDuration, successRate,
      totalOps: filteredLogs.length, completedOps: completed.length, failedOps: failed.length,
      uniqueEntities, totalScrapeCredits, totalMapCredits, totalTokensIn, totalTokensOut,
      totalPagesDiscovered, totalPagesScraped,
    };
  }, [filteredLogs]);

  const costByOperation = useMemo(() => {
    const map: Record<string, { firecrawlCost: number; aiCost: number }> = {};
    filteredLogs.forEach(l => {
      if (!map[l.operation]) map[l.operation] = { firecrawlCost: 0, aiCost: 0 };
      const fc = ((l.firecrawl_map_credits || 0) + (l.firecrawl_scrape_credits || 0)) * 0.00566;
      const ai = ((l.openai_tokens_input || 0) * 0.15 / 1_000_000) + ((l.openai_tokens_output || 0) * 3.50 / 1_000_000);
      map[l.operation].firecrawlCost += fc;
      map[l.operation].aiCost += ai;
    });
    return Object.entries(map).map(([name, d]) => ({
      name,
      firecrawlCost: Number(d.firecrawlCost.toFixed(4)),
      aiCost: Number(d.aiCost.toFixed(4)),
    }));
  }, [filteredLogs]);

  const costByDay = useMemo(() => {
    const map: Record<string, number> = {};
    filteredLogs.forEach(l => {
      const day = l.created_at.split('T')[0];
      map[day] = (map[day] || 0) + Number(l.estimated_cost_usd || 0);
    });
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, cost]) => ({ date: date.slice(5), cost: Number(cost.toFixed(4)) }));
  }, [filteredLogs]);

  const costBreakdown = useMemo(() => {
    const fcCost = filteredLogs.reduce((s, l) => s + ((l.firecrawl_map_credits || 0) + (l.firecrawl_scrape_credits || 0)) * 0.00566, 0);
    const aiCost = filteredLogs.reduce((s, l) => s + ((l.openai_tokens_input || 0) * 0.15 / 1_000_000) + ((l.openai_tokens_output || 0) * 3.50 / 1_000_000), 0);
    const groundingCost = filteredLogs.filter(l => l.operation === 'web_search').length * 0.035;
    return [
      { name: 'Firecrawl', value: Number(fcCost.toFixed(4)) },
      { name: 'Gemini AI', value: Number(aiCost.toFixed(4)) },
      { name: 'Search Grounding', value: Number(groundingCost.toFixed(4)) },
    ].filter(d => d.value > 0);
  }, [filteredLogs, stats.totalCost]);

  const opsByType = useMemo(() => {
    const map: Record<string, { completed: number; failed: number }> = {};
    filteredLogs.forEach(l => {
      if (!map[l.operation]) map[l.operation] = { completed: 0, failed: 0 };
      if (l.status === 'completed') map[l.operation].completed++;
      else map[l.operation].failed++;
    });
    return Object.entries(map).map(([name, data]) => ({ name, ...data }));
  }, [filteredLogs]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-12 space-y-4">
        <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground" />
        <h3 className="text-lg font-medium">No enrichment data yet</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Cost tracking data will appear here once you run enrichment operations (Web Search, Deep Research, or Crawl) on leads or companies.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Lead Enrichment Analytics</h2>
          <p className="text-sm text-muted-foreground">Cost tracking & operational KPIs</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4" />
              <span className="text-xs font-medium">Total Cost</span>
            </div>
            <p className="text-2xl font-bold">{cs}{stats.totalCost.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">{stats.totalOps} operations</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-xs font-medium">Avg Cost/Entity</span>
            </div>
            <p className="text-2xl font-bold">{cs}{stats.avgCostPerEntity.toFixed(3)}</p>
            <p className="text-xs text-muted-foreground">{stats.uniqueEntities} entities enriched</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Activity className="h-4 w-4" />
              <span className="text-xs font-medium">Success Rate</span>
            </div>
            <p className="text-2xl font-bold">{stats.successRate.toFixed(0)}%</p>
            <p className="text-xs text-muted-foreground">{stats.failedOps} failed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Clock className="h-4 w-4" />
              <span className="text-xs font-medium">Avg Duration</span>
            </div>
            <p className="text-2xl font-bold">{stats.avgDuration.toFixed(0)}s</p>
            <p className="text-xs text-muted-foreground">per operation</p>
          </CardContent>
        </Card>
      </div>

      {/* Enrichment Pipeline */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Enrichment Pipeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm">
            {[
              { label: 'Total Leads', count: leadCounts.total, color: 'bg-muted' },
              { label: 'Web Searched', count: leadCounts.webSearched, color: 'bg-primary' },
              { label: 'Deep Researched', count: leadCounts.deepResearched, color: 'bg-accent' },
              { label: 'Crawled', count: leadCounts.crawled, color: 'bg-[hsl(var(--ring))]' },
            ].map((stage, i) => (
              <React.Fragment key={stage.label}>
                {i > 0 && <span className="text-muted-foreground">→</span>}
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-secondary">
                  <div className={`w-2 h-2 rounded-full ${stage.color}`} />
                  <span className="font-medium">{stage.count}</span>
                  <span className="text-muted-foreground hidden sm:inline">{stage.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Cost by Operation */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Cost by Operation</CardTitle>
          </CardHeader>
          <CardContent>
            {costByOperation.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
              <BarChart data={costByOperation}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(222, 20%, 18%)" />
                  <XAxis dataKey="name" tick={{ fill: 'hsl(215, 16%, 56%)', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'hsl(215, 16%, 56%)', fontSize: 12 }} tickFormatter={(v) => `${cs}${v}`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'hsl(222, 47%, 10%)', border: '1px solid hsl(222, 20%, 18%)', borderRadius: '6px' }}
                    labelStyle={{ color: 'hsl(210, 40%, 92%)' }}
                    formatter={(v: number, name: string) => [`${cs}${v.toFixed(4)}`, name === 'firecrawlCost' ? 'Firecrawl' : 'Gemini AI']}
                  />
                  <Legend formatter={(value) => value === 'firecrawlCost' ? 'Firecrawl' : 'Gemini AI'} />
                  <Bar dataKey="firecrawlCost" stackId="cost" fill="hsl(199, 89%, 48%)" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="aiCost" stackId="cost" fill="hsl(262, 83%, 58%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-8">No data</p>}
          </CardContent>
        </Card>

        {/* Cost Breakdown */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Firecrawl vs Gemini AI Cost</CardTitle>
          </CardHeader>
          <CardContent>
            {costBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={costBreakdown} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${cs}${value}`}>
                    {costBreakdown.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: 'hsl(222, 47%, 10%)', border: '1px solid hsl(222, 20%, 18%)', borderRadius: '6px' }}
                    formatter={(v: number) => [`${cs}${v.toFixed(4)}`, 'Cost']}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-8">No data</p>}
          </CardContent>
        </Card>
      </div>

      {/* Cost Trend */}
      {costByDay.length > 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Daily Cost Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={costByDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(222, 20%, 18%)" />
                <XAxis dataKey="date" tick={{ fill: 'hsl(215, 16%, 56%)', fontSize: 11 }} />
                <YAxis tick={{ fill: 'hsl(215, 16%, 56%)', fontSize: 11 }} tickFormatter={(v) => `${cs}${v}`} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'hsl(222, 47%, 10%)', border: '1px solid hsl(222, 20%, 18%)', borderRadius: '6px' }}
                  formatter={(v: number) => [`${cs}${v.toFixed(4)}`, 'Cost']}
                />
                <Line type="monotone" dataKey="cost" stroke="hsl(199, 89%, 48%)" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Operations by Type & Efficiency */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Operations by Type</CardTitle>
          </CardHeader>
          <CardContent>
            {opsByType.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={opsByType}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(222, 20%, 18%)" />
                  <XAxis dataKey="name" tick={{ fill: 'hsl(215, 16%, 56%)', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'hsl(215, 16%, 56%)', fontSize: 12 }} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(222, 47%, 10%)', border: '1px solid hsl(222, 20%, 18%)', borderRadius: '6px' }} />
                  <Legend />
                  <Bar dataKey="completed" fill="hsl(142, 71%, 45%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="failed" fill="hsl(0, 84%, 60%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-8">No data</p>}
          </CardContent>
        </Card>

        {/* Resource Usage */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Resource Usage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-md bg-secondary">
                <p className="text-muted-foreground text-xs">Firecrawl Map Credits</p>
                <p className="text-lg font-semibold">{stats.totalMapCredits}</p>
              </div>
              <div className="p-3 rounded-md bg-secondary">
                <p className="text-muted-foreground text-xs">Firecrawl Scrape Credits</p>
                <p className="text-lg font-semibold">{stats.totalScrapeCredits}</p>
              </div>
              <div className="p-3 rounded-md bg-secondary">
                <p className="text-muted-foreground text-xs">Gemini Tokens (In)</p>
                <p className="text-lg font-semibold">{(stats.totalTokensIn / 1000).toFixed(1)}k</p>
              </div>
              <div className="p-3 rounded-md bg-secondary">
                <p className="text-muted-foreground text-xs">Gemini Tokens (Out)</p>
                <p className="text-lg font-semibold">{(stats.totalTokensOut / 1000).toFixed(1)}k</p>
              </div>
              <div className="p-3 rounded-md bg-secondary">
                <p className="text-muted-foreground text-xs">Pages Discovered</p>
                <p className="text-lg font-semibold">{stats.totalPagesDiscovered}</p>
              </div>
              <div className="p-3 rounded-md bg-secondary">
                <p className="text-muted-foreground text-xs">Pages Scraped</p>
                <p className="text-lg font-semibold">{stats.totalPagesScraped}</p>
                <p className="text-xs text-muted-foreground">
                  {stats.totalPagesDiscovered > 0
                    ? `${((stats.totalPagesScraped / stats.totalPagesDiscovered) * 100).toFixed(0)}% efficiency`
                    : ''}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Failures */}
      {filteredLogs.filter(l => l.status === 'failed').length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive" />
              Recent Failures
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {filteredLogs
                .filter(l => l.status === 'failed')
                .slice(0, 10)
                .map(l => (
                  <div key={l.id} className="flex items-start justify-between text-sm p-2 rounded bg-secondary">
                    <div>
                      <span className="font-medium">{l.operation}</span>
                      <span className="text-muted-foreground ml-2">({l.entity_type})</span>
                      {l.error_message && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{l.error_message}</p>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(l.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
